// Page scroll lock shared by every overlay, counted per holder: the page scrolls again only when the last
// holder releases, so closing a sheet over a modal doesn't unlock the modal's page. overscroll-behavior stops
// scroll chaining / pull-to-refresh behind the overlay; `touch` also blocks touch panning (bottom sheets).
// `dismiss` marks an overlay that system back closes (the shells ask through closeTopOverlay).
const holders = new Map()
const overlayListeners = new Set()
let reportedOpen = false

function scrollbarWidth() {
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth)
}

function apply() {
  const html = document.documentElement
  const body = document.body
  const touch = [ ...holders.values() ].some((options) => options.touch)
  if (holders.size === 0) {
    html.style.overflow = html.style.overscrollBehavior = html.style.touchAction = ""
    body.style.overflow = body.style.touchAction = body.style.paddingRight = ""
    return
  }
  if (html.style.overflow !== "hidden") body.style.paddingRight = `${scrollbarWidth()}px`
  html.style.overflow = body.style.overflow = "hidden"
  html.style.overscrollBehavior = "none"
  html.style.touchAction = body.style.touchAction = touch ? "none" : ""
}

export function lockScroll(owner, { touch = false, dismiss = null } = {}) {
  holders.delete(owner)
  holders.set(owner, { touch, dismiss })
  apply()
  reportOverlay()
}

export function unlockScroll(owner) {
  if (!holders.delete(owner)) return
  apply()
  reportOverlay()
}

function dismissible() {
  return [ ...holders.values() ].filter((options) => options.dismiss)
}

function reportOverlay(force = false) {
  const open = dismissible().length > 0
  if (open === reportedOpen && !force) return
  reportedOpen = open
  overlayListeners.forEach((listener) => listener(open))
}

// Told whenever "a back-dismissible overlay is open" flips (the native shells route system back on it).
export function onOverlayChange(listener) {
  overlayListeners.add(listener)
  return () => overlayListeners.delete(listener)
}

// System back: close the most recently opened dismissible overlay. It stops counting at once (close animations
// release the lock later), and the state is re-reported so the shell knows whether another one is still open.
export function closeTopOverlay() {
  const top = dismissible().at(-1)
  if (!top) {
    reportOverlay(true)
    return false
  }
  const { dismiss } = top
  top.dismiss = null
  reportOverlay(true)
  dismiss()
  return true
}

export function scrollLocked() {
  return holders.size > 0
}

// A holder whose element left the page (a visit away with the overlay open) can't release any more.
export function releaseDetachedHolders() {
  let changed = false
  for (const owner of holders.keys()) {
    if (owner?.element && !owner.element.isConnected) changed = holders.delete(owner) || changed
  }
  if (changed) {
    apply()
    reportOverlay()
  }
}
